import { ref, watch, computed } from "@vue/composition-api";
import axios from "@/libs/axios";
import store from "@/store";
import { debounce } from 'lodash';

export default function useList() {
  const tableColumns = [
    { key: "actions", label: "actions.name", sortable: false },
    { key: "reference_number", label: "simulacrum.reference_number", sortable: true },
    { key: "sector.name", label: "training.region", sortable: false },
    { key: "training_type", label: "training.type", sortable: false },
    { key: "training_topic", label: "training.training_topic", sortable: false },
    { key: "training_modality", label: "training.mode", sortable: false },
   /*  { key: "guests", label: "training.number_of_guests", sortable: false },
    { key: "attendees", label: "training.number_of_attendees", sortable: false }, */
    /* { key: "cost", label: "training.cost", sortable: false }, */
    /* { key: "supplier", label: "training.supplier_name", sortable: false }, */
    { key: "assiged_to", label: "risk.status_assigned", sortable: false },
    { key: "in_review_by", label: "risk.status_in_review", sortable: false },
    { key: "reviewed_to", label: "testing.statusApprovedCanceledRejectedReagined", sortable: false },
    { key: 'registration_date', label: 'date.created_at', sortable: true },
  ];

  store.dispatch("unit/getAll");
  store.dispatch("center/getAll");
  store.dispatch("sector/getAll");
  store.dispatch('region/getAll');
  store.dispatch("municipality/getAll");
  store.dispatch("user/getAll");


  const unitOptions = computed(() => store.getters["unit/getSelectedItems"]);
  const userOptions = computed(() => store.getters["user/getSelectedItems"] || []);
  const countryOptions = computed(() => store.getters["country/getSelectedItems"]);
  const municipalityOptions = computed(() => store.getters["municipality/getSelectedItems"]);
  const centerOptions = computed(() => store.getters["center/getSelectedItems"]);
  const sectorOptions = computed(() => store.getters["sector/getSelectedItems"]);
  const regionOptions = computed(() => store.getters['region/getSelectedItems'])

  const perPage = ref(10);
  const currentPage = ref(1);
  const totalList = ref(0);
  const searchQuery = ref("");
  const sortBy = ref("created_at");
  const isSortDirDesc = ref(true);
  const filters = ref({
    units: [],
    users: [],
    countries: [],
    municipalities: [],
    status: [],
    responsable: [],
    assigned: [],
    startDate: null,
    endDate: null,
    rems: null,
    sirh: null,
  });

  const filtersCount = computed(() => {
    let count = 0;
    if (filters.value.units.length) count++;
    if (filters.value.users.length) count++;
    if (filters.value.countries.length) count++;
    if (filters.value.municipalities.length) count++;
    if (filters.value.status.length) count++;
    if (filters.value.responsable.length) count++;
    if (filters.value.assigned.length) count++;
    if (filters.value.startDate || filters.value.endDate) count++;
    if (filters.value.rems !== null && filters.value.rems !== undefined) count++;
    if (filters.value.sirh !== null && filters.value.sirh !== undefined) count++;
    return count;
  });

  const clearSearchFilters = () => {
    filters.value = {
      units: [],
      users: [],
      countries: [],
      municipalities: [],
      status: [],
      responsable: [],
      assigned: [],
      startDate: null,
      endDate: null,
      rems: null,
      sirh: null,
    };
    currentPage.value = 1;
    refetchData();
  };

  const buildFilters = () => {
    filters.value = {
      units: filters.value.units || [],
      users: filters.value.users || [],
      countries: filters.value.countries || [],
      municipalities: filters.value.municipalities || [],
      status: filters.value.status || [],
      responsable: filters.value.responsable || [],
      assigned: filters.value.assigned || [],
      startDate: filters.value.startDate,
      endDate: filters.value.endDate,
      rems: filters.value.rems,
      sirh: filters.value.sirh,
    };
    currentPage.value = 1;
    refetchData();
  };

  const dataMeta = computed(() => {
    const localItemsCount = refListTable.value ? refListTable.value.localItems.length : 0;
    return {
      from: perPage.value * (currentPage.value - 1) + (localItemsCount ? 1 : 0),
      to: perPage.value * (currentPage.value - 1) + localItemsCount,
      of: totalList.value,
    };
  });

  const refListTable = ref(null);
  const perPageOptions = [10, 25, 50, 100];

  const fetchList = (ctx) => new Promise((resolve, reject) => {
    const queryParams = {
      page: currentPage.value,
      per_page: perPage.value,
      sort_by: sortBy.value,
      sort_desc: isSortDirDesc.value ? 1 : 0,
      q: searchQuery.value,
      units: filters.value.units,
      users: filters.value.users,
      countries: filters.value.countries,
      municipalities: filters.value.municipalities,
      status_form: filters.value.status,
      responsable: filters.value.responsable,
      assigned: filters.value.assigned,
      date_from: filters.value.startDate,
      date_to: filters.value.endDate,
      rems: filters.value.rems,
      sirh: filters.value.sirh,
    };

    axios
      .get("/v1/trainings", { params: queryParams })
      .then((response) => {
        const { data, meta } = response.data;
        resolve(data);
        totalList.value = meta.total;
      })
      .catch((error) => reject(error));
  });

  const refetchData = () => { refListTable.value.refresh(); };

  watch([currentPage, perPage, sortBy, isSortDirDesc], () => { refetchData(); });
  watch([searchQuery], debounce(() => { refetchData(); }, 500));

  const formatDateAssigned = (value) => {
    if (!value) return "";
    const date = new Date(value);
    return new Intl.DateTimeFormat("default", {
      year: "numeric",
      month: "2-digit",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
    }).format(date);
  };

  const getUserAsigned = (lastStatus) => {
    if (!lastStatus) {
      return '-';
    }

    const { user } = lastStatus;

    return (user) ? `${user.user} - ${user.full_name}` : '-';
  }

  const getUserReview = (penetrationTestingStatus) => {
    const statusReviewTesting = penetrationTestingStatus.find(
      pnts => (pnts.status.name === 'in_review')
    );

    if (!statusReviewTesting) {
      return '-';
    }

    const { user_validation } = statusReviewTesting;

    return (user_validation) ? `${user_validation.user} - ${user_validation.full_name}` : '-';
  }

  const getUserValidator = (lastStatus) => {
    if (!lastStatus) {
      return '-';
    }

    const validationStatuses = ['approved','canceled','rejected'];
    const { status } = lastStatus;

    if (!validationStatuses.includes(status.name)) {
      return '-';
    }

    const { user_validation } = lastStatus;

    return (user_validation) ? `${user_validation.user} - ${user_validation.full_name}` : '-';
  }

  return {
    fetchList,
    tableColumns,
    perPage,
    currentPage,
    totalList,
    dataMeta,
    perPageOptions,
    searchQuery,
    sortBy,
    isSortDirDesc,
    refListTable,
    refetchData,
    formatDateAssigned,
    filters,
    filtersCount,
    clearSearchFilters,
    buildFilters,
    unitOptions,
    userOptions,
    countryOptions,
    municipalityOptions,
    centerOptions,
    sectorOptions,
    regionOptions,
    getUserAssigned: getUserAsigned,
    getUserReview,
    getUserValidator
  };
}


